// T46: Wiadomości — odebrane (każdy), wysłane z listą "kto przeczytał" i formularz (nadawcy).
// Bez usuwania.
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Modal, fullName, useToast, RoleBadge, ROLE_LABELS } from '../components/UI';
import api from '../utils/api';

const AUD = { ALL: 'wszyscy', ROLE: 'rola', TEAM: 'moi podwładni', USERS: 'wybrane osoby' };
const fmt = (d) => new Date(d).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export default function MessagesPage() {
  const { canSendMessages, isAdmin } = useAuth();
  const ctx = useOutletContext() || {};
  const [tab, setTab] = useState('inbox');
  const [inbox, setInbox] = useState([]);
  const [sent, setSent] = useState([]);
  const [open, setOpen] = useState(null);
  const [compose, setCompose] = useState(false);
  const { show, ToastComponent } = useToast();

  const load = useCallback(async () => {
    try { setInbox(await api.getInbox()); } catch (e) { show(e.message, 'error'); }
    if (canSendMessages) { try { setSent(await api.getSentMessages()); } catch { setSent([]); } }
  }, [canSendMessages]);
  useEffect(() => { load(); }, [load]);

  const openMsg = async (m) => {
    setOpen(m);
    if (!m.readAt) {
      try { await api.markRead(m.id); setInbox(list => list.map(x => x.id === m.id ? { ...x, readAt: new Date().toISOString() } : x)); ctx.refreshUnread && ctx.refreshUnread(); } catch { /* ok */ }
    }
  };
  const unreadCount = inbox.filter(m => !m.readAt).length;

  return (
    <div className="flex flex-col gap-14">
      <div className="page-header">
        <h2 className="page-title">Wiadomości{unreadCount > 0 && <span style={{ fontSize: 12, color: '#EF4444', marginLeft: 8 }}>({unreadCount} nowe)</span>}</h2>
        {canSendMessages && <button className="btn btn-primary" onClick={() => setCompose(true)}>+ Nowa wiadomość</button>}
      </div>
      {canSendMessages && (
        <div className="filter-pills">
          <button className={`filter-pill ${tab === 'inbox' ? 'active' : ''}`} onClick={() => setTab('inbox')}>Odebrane ({inbox.length})</button>
          <button className={`filter-pill ${tab === 'sent' ? 'active' : ''}`} onClick={() => setTab('sent')}>Wysłane ({sent.length})</button>
        </div>
      )}

      {tab === 'inbox' && (
        <div className="card">
          {inbox.length === 0 && <div style={{ color: '#94A3B8', fontSize: 12, padding: 10 }}>Brak wiadomości.</div>}
          {inbox.map(m => (
            <div key={m.id} onClick={() => openMsg(m)} style={{ display: 'flex', gap: 10, padding: '10px 6px', borderBottom: '1px solid #F1F5F9', cursor: 'pointer', background: m.readAt ? 'transparent' : '#FFFBEB' }}>
              <div style={{ width: 8, marginTop: 6 }}>{!m.readAt && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 99, background: '#EF4444' }} />}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: m.readAt ? 600 : 800 }}>{m.title}</div>
                <div style={{ fontSize: 11, color: '#64748B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.content}</div>
              </div>
              <div style={{ fontSize: 10, color: '#94A3B8', textAlign: 'right', whiteSpace: 'nowrap' }}>{fullName(m.sender)}<br />{fmt(m.createdAt)}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'sent' && (
        <div className="card">
          {sent.length === 0 && <div style={{ color: '#94A3B8', fontSize: 12, padding: 10 }}>Nic jeszcze nie wysłano.</div>}
          {sent.map(m => <SentRow key={m.id} m={m} />)}
        </div>
      )}

      {open && (
        <Modal title={open.title} onClose={() => setOpen(null)}>
          <div style={{ fontSize: 11, color: '#64748B', marginBottom: 8 }}>Od: <b>{fullName(open.sender)}</b> · {fmt(open.createdAt)}</div>
          <div style={{ fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{open.content}</div>
          <div className="flex justify-end" style={{ marginTop: 14 }}><button className="btn btn-primary" onClick={() => setOpen(null)}>Zamknij</button></div>
        </Modal>
      )}
      {compose && <ComposeModal isAdmin={isAdmin} onClose={() => setCompose(false)} onSent={(msg) => { show(msg); setCompose(false); setTab('sent'); load(); }} />}
      {ToastComponent}
    </div>
  );
}

function SentRow({ m }) {
  const [expanded, setExpanded] = useState(false);
  const pct = m.total ? Math.round(100 * m.readCount / m.total) : 0;
  return (
    <div style={{ padding: '10px 6px', borderBottom: '1px solid #F1F5F9' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{m.title} <span style={{ fontSize: 10, color: '#64748B', fontWeight: 400 }}>→ {AUD[m.audience]}{m.role ? `: ${ROLE_LABELS[m.role] || m.role}` : ''}</span></div>
          <div style={{ fontSize: 11, color: '#64748B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.content}</div>
        </div>
        <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: pct === 100 ? '#059669' : '#D97706' }}>przeczytało {m.readCount} / {m.total}</div>
          <div style={{ fontSize: 10, color: '#94A3B8' }}>{fmt(m.createdAt)} · {expanded ? '▲' : '▼'}</div>
        </div>
      </div>
      {expanded && (
        <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 4, fontSize: 11 }}>
          {m.recipients.map(r => (
            <div key={r.user.id} style={{ color: r.readAt ? '#059669' : '#B45309' }}>
              {r.readAt ? '✓' : '○'} {fullName(r.user)} <RoleBadge role={r.user.role} /> {r.readAt ? <span style={{ color: '#94A3B8' }}>{fmt(r.readAt)}</span> : <span style={{ color: '#94A3B8' }}>nie przeczytał</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ComposeModal({ isAdmin, onClose, onSent }) {
  const [f, setF] = useState({ title: '', content: '', audience: isAdmin ? 'ALL' : 'TEAM', role: 'EVENT', userIds: [] });
  const [people, setPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { api.getMessageRecipients().then(r => setPeople(r.users)).catch(() => setPeople([])); }, []);
  const toggle = (id) => setF(x => ({ ...x, userIds: x.userIds.includes(id) ? x.userIds.filter(i => i !== id) : [...x.userIds, id] }));
  const ready = f.title.trim() && f.content.trim().length >= 3 && (f.audience !== 'USERS' || f.userIds.length > 0);
  const send = async () => {
    setBusy(true); setError('');
    try { const r = await api.sendMessage(f); onSent(r.message); }
    catch (e) { setError(e.message); }
    setBusy(false);
  };
  const count = f.audience === 'USERS' ? f.userIds.length : (f.audience === 'TEAM' ? people.length : null);
  return (
    <Modal title="Nowa wiadomość" onClose={onClose} wide dirty={!!(f.title.trim() || f.content.trim() || f.userIds.length)}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {error && <div className="login-error">{error}</div>}
        <div className="input-group"><label>Tytuł *</label><input value={f.title} onChange={e => setF({ ...f, title: e.target.value })} placeholder="np. Rozliczenie godzin" autoFocus /></div>
        <div className="input-group"><label>Treść *</label><textarea rows={5} value={f.content} onChange={e => setF({ ...f, content: e.target.value })} placeholder="Najbliższe rozliczenie będzie 30.09. Pamiętajcie o wpisaniu wszystkich godzin do poniedziałku." /></div>
        <div className="flex gap-8 flex-wrap">
          <div className="input-group" style={{ minWidth: 200 }}><label>Do kogo</label>
            <select value={f.audience} onChange={e => setF({ ...f, audience: e.target.value })}>
              {isAdmin && <option value="ALL">Wszyscy pracownicy</option>}
              {isAdmin && <option value="ROLE">Wszyscy z roli…</option>}
              {!isAdmin && <option value="TEAM">Moi podwładni</option>}
              <option value="USERS">Wybrane osoby</option>
            </select>
          </div>
          {f.audience === 'ROLE' && (
            <div className="input-group" style={{ minWidth: 160 }}><label>Rola</label>
              <select value={f.role} onChange={e => setF({ ...f, role: e.target.value })}>{Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
            </div>
          )}
        </div>
        {f.audience === 'USERS' && (
          <div className="card" style={{ background: '#F8FAFC', padding: 10, maxHeight: 220, overflowY: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 4, fontSize: 11 }}>
              {people.map(u => (
                <label key={u.id} style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
                  <input type="checkbox" checked={f.userIds.includes(u.id)} onChange={() => toggle(u.id)} /> {fullName(u)} <span style={{ color: '#94A3B8' }}>({ROLE_LABELS[u.role] || u.role})</span>
                </label>
              ))}
              {people.length === 0 && <span style={{ color: '#94A3B8' }}>Brak osób, do których możesz pisać.</span>}
            </div>
          </div>
        )}
        <div style={{ fontSize: 11, color: '#64748B' }}>
          Adresat zobaczy wiadomość w oknie zaraz po zalogowaniu (zamknięcie możliwe po 5 s) i w zakładce „Wiadomości". Zobaczysz, kto i kiedy przeczytał. Wiadomości nie da się usunąć.
          {count !== null && <b> Adresatów: {count}.</b>}
        </div>
        <div className="flex gap-6 justify-end"><button className="btn btn-outline" onClick={onClose}>Anuluj</button><button className="btn btn-primary" onClick={send} disabled={!ready || busy}>{busy ? 'Wysyłanie…' : 'Wyślij'}</button></div>
      </div>
    </Modal>
  );
}
